<template>
  <v-container class="my-1">
    <v-card>
      <v-card-title class="page-head">
        <div class="ph-left">
          <div class="ph-icon"><v-icon color="#1565c0">mdi-clipboard-text</v-icon></div>
          <div>
            <div class="ph-title">Child Assessment</div>
            <div class="ph-sub">Track health &amp; nutrition follow-ups</div>
          </div>
        </div>
        <v-spacer />
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search..."
          outlined
          dense
          hide-details
          class="ph-search"
        />
        <v-btn class="ph-btn" @click="openNew">
          <v-icon left small>mdi-plus</v-icon>
          New Assessment
        </v-btn>
      </v-card-title>

      <v-data-table
        class="elevation-4"
        :search="search"
        :headers="headers"
        :items="assessments"
        item-value="id"
        fixed-header
        :height="500"
        :mobile-breakpoint="600"
      >
        <template v-slot:item.index="{ index }">
          <span class="cell-muted">{{ index + 1 }}</span>
        </template>

        <template v-slot:item.childname="{ item }">
          <span class="cell-name">{{ item.childname }}</span>
        </template>

        <template v-slot:item.nutrition_status="{ item }">
          <v-chip
            small
            :color="['Excellent','Good'].includes(item.nutrition_status) ? '#e8f5e9' : (item.nutrition_status === 'Fair' ? '#fff8e1' : '#ffebee')"
            :text-color="['Excellent','Good'].includes(item.nutrition_status) ? '#2e7d32' : (item.nutrition_status === 'Fair' ? '#f57f17' : '#c62828')"
          >
            {{ item.nutrition_status || '—' }}
          </v-chip>
        </template>

        <template v-slot:item.notes="{ item }">
          <span class="pre-line break-anywhere">{{ item.notes }}</span>
        </template>

        <template v-slot:item.more="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="#1565c0" v-bind="attrs" v-on="on" @click="show(item)">
                <v-icon small>mdi-information-outline</v-icon>
              </v-btn>
            </template>
            <span>View details</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="44" color="#b0bec5">mdi-clipboard-text-outline</v-icon>
            <div class="empty-text">No assessments found</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Assessment detail dialog -->
    <v-dialog v-model="diag" scrollable max-width="520px">
      <v-card v-if="selectedAssessment" id="printMe">
        <div class="dlg-head">
          <div class="dlg-head-icon"><v-icon color="#fff">mdi-clipboard-text</v-icon></div>
          <div>
            <div class="dlg-title">Assessment Details</div>
            <div class="dlg-sub">{{ selectedAssessment.childname }}</div>
          </div>
          <v-spacer />
          <v-btn icon small class="dlg-close" @click="diag = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="d-flex justify-center pt-5 pb-2" style="gap: 48px;">
          <div class="text-center">
            <v-avatar size="80" class="avatar-ring lg">
              <v-img v-if="selectedAssessment.motherimage" :src="uploadBaseUrl + selectedAssessment.motherimage" />
              <v-icon v-else size="36" color="#90a4ae">mdi-account-heart</v-icon>
            </v-avatar>
            <div class="cell-muted mt-1">Care Giver</div>
          </div>
          <div class="text-center">
            <v-avatar size="80" class="avatar-ring lg">
              <v-img v-if="selectedAssessment.childimage" :src="uploadBaseUrl + selectedAssessment.childimage" />
              <v-icon v-else size="36" color="#90a4ae">mdi-human-child</v-icon>
            </v-avatar>
            <div class="cell-muted mt-1">Child</div>
          </div>
        </div>

        <v-card-text style="height: 300px;">
          <v-simple-table>
            <tbody>
              <tr><th>Care Giver</th><td>{{ selectedAssessment.mothername }}</td></tr>
              <tr><th>Care Giver Sex</th><td>{{ selectedAssessment.mother_sex }}</td></tr>
              <tr><th>Care Giver Contact</th><td>{{ selectedAssessment.contact }}</td></tr>
              <tr><th>Child Name</th><td>{{ selectedAssessment.childname }}</td></tr>
              <tr><th>Child Sex</th><td>{{ selectedAssessment.child_sex }}</td></tr>
              <tr><th>Child Age</th><td>{{ selectedAssessment.age }}</td></tr>
              <tr><th>Visit Date</th><td>{{ selectedAssessment.visit_date }}</td></tr>
              <tr><th>Is Sick</th><td>{{ selectedAssessment.is_sick }}</td></tr>
              <tr><th>Sickness Details</th><td class="pre-line break-anywhere">{{ selectedAssessment.sickness_details }}</td></tr>
              <tr><th>Nutrition Status</th><td>{{ selectedAssessment.nutrition_status }}</td></tr>
              <tr><th>Mental Status</th><td>{{ selectedAssessment.mental_status }}</td></tr>
              <tr><th>Action Taken</th><td class="pre-line break-anywhere">{{ selectedAssessment.action_taken }}</td></tr>
              <tr><th>Next Visit Date</th><td>{{ selectedAssessment.next_visit_date }}</td></tr>
              <tr><th>Assessed By</th><td>{{ selectedAssessment.assessed_by }}</td></tr>
              <tr><th>Notes</th><td class="pre-line break-anywhere">{{ selectedAssessment.notes }}</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="680" persistent>
      <v-card>
        <div class="dlg-head">
          <div class="dlg-head-icon">
            <v-icon color="#fff">{{ dialogMode === 'create' ? 'mdi-clipboard-plus' : 'mdi-clipboard-edit' }}</v-icon>
          </div>
          <div>
            <div class="dlg-title">{{ dialogMode === 'create' ? 'New Assessment' : 'Edit Assessment' }}</div>
            <div class="dlg-sub">Health &amp; nutrition follow-up</div>
          </div>
          <v-spacer />
          <v-btn icon small class="dlg-close" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="pt-5">
          <v-form>
            <div class="form-section">Child &amp; Visit</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <label class="cell-muted d-block mb-1">Select Child</label>
                <Select2
                  v-model="form.child_id"
                  :options="childoption"
                  id="assessment-child"
                  :settings="{ width: '100%' }"
                  @change="onChildChange"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="cell-muted d-block mb-1">Select Care Giver</label>
                <Select2
                  v-model="form.mother_id"
                  :options="motheroption"
                  id="assessment-mother"
                  :settings="{ width: '100%' }"
                  @change="onMotherChange"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.assessment_date" type="date" label="Assessment Date" outlined dense prepend-inner-icon="mdi-calendar" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.next_followup_date" type="date" label="Next Follow-up Date" outlined dense prepend-inner-icon="mdi-calendar-clock" />
              </v-col>
            </v-row>

            <div class="form-section">Health Status</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select v-model="form.nutritionstatus" :items="healthStatusOptions" label="Nutrition / Health Status" outlined dense prepend-inner-icon="mdi-food-apple-outline" />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="form.issick" :items="issickOptions" label="Is Child Sick?" outlined dense prepend-inner-icon="mdi-thermometer" />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="form.mentalstatus" :items="mentalStatusOptions" label="Mental Status" outlined dense prepend-inner-icon="mdi-brain" />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.sicknessdetails" label="Sickness Details" auto-grow rows="2" outlined dense prepend-inner-icon="mdi-medical-bag" />
              </v-col>
            </v-row>

            <div class="form-section">Outcome</div>
            <v-row dense>
              <v-col cols="12">
                <v-textarea v-model="form.actiontaken" label="Action Taken" auto-grow rows="2" outlined dense prepend-inner-icon="mdi-clipboard-check-outline" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.assessedby" label="Assessed By" outlined dense prepend-inner-icon="mdi-account-check-outline" />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.notes" label="Notes" auto-grow rows="3" outlined dense prepend-inner-icon="mdi-text-box-outline" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="dlg-actions">
          <v-spacer />
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="#0d2d63" dark @click="save">
            <v-icon left small>mdi-content-save-outline</v-icon>
            {{ dialogMode === 'create' ? 'Save Assessment' : 'Save Changes' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import Select2 from 'v-select2-component';

const API_URL = process.env.VUE_APP_API_URL;
const IMAGE_BASE_MOTHERS = process.env.VUE_APP_IMAGE_BASE;

export default {
  name: 'ChildAssessmentVue',
  components: {
    Select2,
  },
  data() {
    return {
      search: '',
      dialog: false,
      dialogMode: 'create',
      diag: false,
      assessments: [],
      motheroption: [],
      childoption: [],
      moreinfo: [],
      selectedAssessment: null,
      form: {
        id: null,
        child_id: null,
        mother_id: null,
        assessment_date: '',
        next_followup_date: '',
        nutritionstatus: '',
        issick: '',
        sicknessdetails: '',
        mentalstatus: '',
        actiontaken: '',
        assessedby: '',
        notes: '',
      },
      uploadBaseUrl: IMAGE_BASE_MOTHERS,
      healthStatusOptions: [
        'Excellent',
        'Good',
        'Fair',
        'Poor',
        'Critical',
        'Unknown'
      ],
      issickOptions: [
        'Yes',
        'No'
      ],
      mentalStatusOptions: [
        'Normal',
        'Mild Issues',
        'Moderate Issues',
        'Severe Issues',
        'Unknown'
      ],
      headers: [
        { text: 'No.', value: 'index' },
        { text: 'Child', value: 'childname' },
        { text: 'Care Giver', value: 'mothername' },
        { text: 'Assessment Date', value: 'visit_date' },
        { text: 'Next Follow-up', value: 'next_visit_date' },
        { text: 'Nutrition Status', value: 'nutrition_status' },
        { text: 'Notes', value: 'notes' },
        { text: 'Show More', value: 'more', sortable: false },
        // { text: 'Actions', value: 'actions', sortable: false },
      ],
    };
  },
  mounted() {
    this.fetchAssessments();
    this.fetchChildren();
  },
  methods: {
    onChildChange(value) {
      this.form.child_id = value;
      this.fetchMothers(value);
    },
    onMotherChange(value) {
      this.form.mother_id = value;
    },
    fetchMothers(childId) {
      axios
        .post(API_URL, { opcion: 45, id: childId, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then((response) => {
          this.motheroption = response.data || [];
          console.log(this.motheroption);
        })
        .catch(() => {
          this.motheroption = [];
        });
    },
    fetchChildren() {
      axios
        .post(API_URL, { opcion: 55, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then((response) => {
          this.childoption = response.data || [];
        })
        .catch(() => {
          this.childoption = [];
        });
    },
    fetchAssessments() {
      axios
        .post(API_URL, { opcion: 20, op: 0, assessedby: (JSON.parse(localStorage.getItem('user') || '{}')).id || '' })
        .then((res) => {
          const payload = res.data;
          // Support both raw array and wrapped object { success: true, data: [...] }
          this.assessments = Array.isArray(payload)
            ? payload
            : Array.isArray(payload && payload.data)
            ? payload.data
            : [];
          console.log('assessments:', this.assessments);
        })
        .catch(() => {
          this.assessments = [];
        });
    },
    openNew() {
      this.dialogMode = 'create';
      this.form = {
        id: null,
        child_id: null,
        mother_id: null,
        assessment_date: '',
        next_followup_date: '',
        nutritionstatus: '',
        issick: '',
        sicknessdetails: '',
        mentalstatus: '',
        actiontaken: '',
        assessedby: '',
        notes: '',
      };
      this.dialog = true;
    },
    edit(item) {
      this.dialogMode = 'edit';
      this.form = {
        id: item.id,
        child_id: item.child_id,
        mother_id: item.mother_id,
        assessment_date: item.assessment_date,
        next_followup_date: item.next_followup_date,
        nutritionstatus: item.nutritionstatus,
        issick: item.issick,
        sicknessdetails: item.sicknessdetails,
        mentalstatus: item.mentalstatus,
        actiontaken: item.actiontaken,
        assessedby: item.assessedby,
        notes: item.notes,
      };
      this.dialog = true;
    },
    save() {
      if (this.dialogMode === 'create') {
        this.createAssessment();
      } else {
        this.updateAssessment();
      }
    },
    createAssessment() {
      const payload = {
        opcion: 20,
        op: 1,
        idga: this.form.id,
        childid: this.form.child_id,
        motherid: this.form.mother_id,
        visitdate: this.form.assessment_date,
        nextvisitdate: this.form.next_followup_date,
        issick: this.form.issick,
        sicknessdetails: this.form.sicknessdetails,
        nutritionstatus: this.form.nutritionstatus,
        mentalstatus: this.form.mentalstatus,
        actiontaken: this.form.actiontaken,
        assessedby: (JSON.parse(localStorage.getItem('user') || '{}')).id || '',
      };
      console.log('Create assessment payload:', payload);
      axios.post(API_URL, payload).then(() => {
        this.dialog = false;
        this.fetchAssessments();
      });
    },
    updateAssessment() {
      const payload = {
        opcion: 20, 
        op: 3,
        idga: this.form.id,
        childid: this.form.child_id,
        motherid: this.form.mother_id,
        visitdate: this.form.assessment_date,
        nextvisitdate: this.form.next_followup_date,
        issick: this.form.issick,
        sicknessdetails: this.form.sicknessdetails,
        nutritionstatus: this.form.nutritionstatus,
        mentalstatus: this.form.mentalstatus,
        actiontaken: this.form.actiontaken,
        assessedby: (JSON.parse(localStorage.getItem('user') || '{}')).id || '',
      };
      console.log('Update assessment payload:', payload);
      axios.post(API_URL, payload).then(() => {
        this.dialog = false;
        this.fetchAssessments();
      });
    },
    remove(item) {
      if (!item || !item.id) return;
      const payload = {
        opcion: 20,
        op: 12,
        id: item.id,
        assessedby: (JSON.parse(localStorage.getItem('user') || '{}')).id || '',
      };
      axios.post(API_URL, payload).then(() => {
        this.fetchAssessments();
      });
    },
    show(item) {
      this.selectedAssessment = item;
      this.diag = true;
    },
  },
};
</script>

<style scoped>
.pre-line {
  white-space: pre-line;
}
.break-anywhere {
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
